import React from "react";
import { Alert } from "reactstrap";
import strings from "../../core/translate";
import RequestEngine from "../../core/RequestEngine";
import { apiErrorMessage } from "../../shared/api-error";
import { formatMileage } from "../../shared/mileage";
import { FormSection, FormGrid } from "../../components/Form/FormKit";
import ButtonLoader from "../../components/ButtonLoader/ButtonLoader";
import {
  ItemStatus,
  currencySymbolOf,
  itemStatusMeta,
  money,
  statusLabel,
  statusTone,
} from "../../shared/work-orders";
// the pills and the staff picker are the work order's own
import "../../assets/css/work-orders.css";

const Detail = ({ label, value }) => (
  <div className="job-card-detail">
    <span className="job-card-detail__label">{label}</span>
    <span className="job-card-detail__value">{value || "-"}</span>
  </div>
);

// The app caps a card at this many stills, plus its walk-around video.
const MAX_PHOTOS = 10;
// The app keeps the vehicle's history short on the card; so does this.
const MAX_PREVIOUS_VISITS = 6;

const isVideo = (path) => /\.(mp4|mov|m4v|webm|3gp|mkv)$/i.test(path || "");

/**
 * One job card, worked the way an Owner or a Manager works it in the app: open
 * (or start) its work order, send mechanics to inspect the car, add or remove
 * its photos and clips, and close it once the car has gone. Under that, what
 * the work order found on this visit and the car's previous visits.
 *
 * The card is reloaded after every step, so what is shown is what the garage
 * holds — the list behind is told through `onChanged`.
 */
class JobCardView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      card: props.jobCard,
      workOrder: null,
      previous: [],
      mechanics: null,
      // which inline panel is open: "inspect", "close" or null
      panel: null,
      chosen: [],
      acting: null,
      deletingId: null,
      error: "",
      success: "",
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.loadWorkOrder();
    this.loadPrevious();
  }

  get base() {
    return `merchants-branches/${this.props.branchId}/job-cards/${this.state.card.id}`;
  }

  get isClosed() {
    return this.state.card.status === "closed";
  }

  reload = async () => {
    try {
      const res = await this.engine.getItem(this.base);
      if (res && res.data) this.setState({ card: res.data });
    } catch (e) {
      // the last good copy stays on screen
    }
    this.loadWorkOrder();
    if (this.props.onChanged) this.props.onChanged();
  };

  // A card that has no work order yet answers 404 — that is "none", not a
  // failure.
  loadWorkOrder = async () => {
    const { card } = this.state;
    const path = card.bookingId
      ? `bookings/${card.bookingId}/work-order`
      : `job-cards/${card.id}/work-order`;
    try {
      const res = await this.engine.getItem(path);
      this.setState({ workOrder: (res && res.data) || null });
    } catch (e) {
      this.setState({ workOrder: null });
    }
  };

  loadPrevious = async () => {
    const { card } = this.state;
    if (!card.customerVehicleId) return;
    try {
      const res = await this.engine.getItem(
        `merchants-branches/${this.props.branchId}/work-orders/by-vehicle/${card.customerVehicleId}`
      );
      const rows = Array.isArray(res && res.data) ? res.data : [];
      this.setState({
        previous: rows
          // this visit's own order is the one shown above, not history
          .filter((row) => row.jobCardId !== card.id)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, MAX_PREVIOUS_VISITS),
      });
    } catch (e) {
      this.setState({ previous: [] });
    }
  };

  /** Runs one step: one at a time, its answer shown under the action bar. */
  act = async (key, run, success) => {
    this.setState({ acting: key, error: "", success: "" });
    try {
      await run();
      this.setState({ acting: null, panel: null, success });
      await this.reload();
      return true;
    } catch (e) {
      this.setState({ acting: null, error: apiErrorMessage(e) });
      return false;
    }
  };

  // ── send to inspect ─────────────────────────────────────────────────────

  openInspect = async () => {
    const { card, panel } = this.state;
    if (panel === "inspect") return this.setState({ panel: null });
    this.setState({
      panel: "inspect",
      chosen: (card.inspectors || []).map((one) => one.id),
    });
    if (this.state.mechanics) return;
    try {
      const res = await this.engine.getItem(
        `merchants-branches/${this.props.branchId}/job-cards/branch-workers`
      );
      const staff = Array.isArray(res && res.data) ? res.data : [];
      // only a mechanic inspects the car
      this.setState({ mechanics: staff.filter((w) => w.role === "mechanic") });
    } catch (e) {
      this.setState({ mechanics: [], error: apiErrorMessage(e) });
    }
  };

  toggleInspector = (id) =>
    this.setState((state) => ({
      chosen: state.chosen.includes(id)
        ? state.chosen.filter((one) => one !== id)
        : [...state.chosen, id],
    }));

  sendToInspect = () =>
    this.act(
      "inspect",
      () =>
        this.engine.patchItem(`${this.base}/inspector`, {
          workerIds: this.state.chosen,
        }),
      strings.inspectors_assigned
    );

  // ── close ───────────────────────────────────────────────────────────────

  /**
   * Closing is the end of the visit — the car is handed back, its booking is
   * finished and its work order closed — so the dialog goes with it.
   */
  closeCard = async () => {
    const closed = await this.act(
      "close",
      () => this.engine.patchItem(`${this.base}/status`, { status: "closed" }),
      strings.job_card_closed_done
    );
    if (closed) this.props.onClose();
  };

  // ── photos ──────────────────────────────────────────────────────────────

  uploadMedia = async (files) => {
    const room = MAX_PHOTOS - (this.state.card.photos || []).length;
    const picked = Array.from(files || []).slice(0, Math.max(room, 0));
    if (picked.length === 0) return;
    await this.act(
      "photo",
      async () => {
        // one request per file, as the app sends them
        for (const file of picked) {
          const form = new FormData();
          form.append("file", file);
          await this.engine.postItemMultipart(`${this.base}/photos`, form);
        }
      },
      strings.photo_uploaded_card
    );
  };

  removeMedia = async (photo) => {
    if (!window.confirm(strings.confirm_remove_attachment)) return;
    this.setState({ deletingId: photo.id });
    await this.act(
      "remove-photo",
      () => this.engine.deleteItem(`${this.base}/photos`, photo.id),
      strings.attachment_removed
    );
    this.setState({ deletingId: null });
  };

  // ── render ──────────────────────────────────────────────────────────────

  mediaUrl = (path) =>
    `${this.props.serverLink}${path}`.replace(/([^:]\/)\/+/g, "$1");

  renderActions() {
    const { card, workOrder, acting } = this.state;
    const busy = Boolean(acting);
    const inspectorNames = (card.inspectors || []).map((w) => w.name).join(", ");

    const tiles = [
      // a closed card's report is opened as it was left, never started anew
      (workOrder || !this.isClosed) && {
        key: "wo",
        icon: "fa fa-wrench",
        tone: "primary",
        label: workOrder ? strings.work_order : strings.create_work_order,
        onClick: () => this.props.onOpenWorkOrder(card),
      },
      !this.isClosed && {
        key: "inspect",
        icon: "nc-icon nc-zoom-split",
        tone: "info",
        label: inspectorNames
          ? `${strings.inspectors}: ${inspectorNames}`
          : strings.send_to_inspect,
        on: this.state.panel === "inspect",
        onClick: this.openInspect,
      },
      !this.isClosed && {
        key: "close",
        icon: "nc-icon nc-check-2",
        tone: "success",
        label: strings.close_job_card,
        on: this.state.panel === "close",
        onClick: () =>
          this.setState((s) => ({ panel: s.panel === "close" ? null : "close" })),
      },
    ].filter(Boolean);

    return (
      <div className="job-card-actions">
        {tiles.map((tile) => (
          <button
            key={tile.key}
            type="button"
            className={`job-card-action is-${tile.tone}${tile.on ? " is-on" : ""}`}
            disabled={busy}
            onClick={tile.onClick}
          >
            <i className={acting === tile.key ? "fa fa-spinner fa-spin" : tile.icon} />
            <span>{tile.label}</span>
          </button>
        ))}
      </div>
    );
  }

  renderPanel() {
    const { panel, mechanics, chosen, acting } = this.state;

    if (panel === "close") {
      return (
        <div className="job-card-panel">
          <p>{strings.confirm_close_job_card}</p>
          <div className="job-card-panel__foot">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => this.setState({ panel: null })}
            >
              {strings.cancel}
            </button>
            <ButtonLoader
              color="success"
              loading={acting === "close"}
              disabled={Boolean(acting)}
              onClick={this.closeCard}
            >
              {strings.close_job_card}
            </ButtonLoader>
          </div>
        </div>
      );
    }

    if (panel === "inspect") {
      return (
        <div className="job-card-panel">
          <p className="job-card-panel__title">{strings.choose_inspectors}</p>
          {mechanics === null && <p className="wo-hint">{strings.loading}…</p>}
          {mechanics && mechanics.length === 0 && (
            <p className="wo-hint">{strings.no_mechanics_on_staff}</p>
          )}
          {(mechanics || []).map((one) => {
            const on = chosen.includes(one.id);
            return (
              <label key={one.id} className={`wo-worker${on ? " is-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => this.toggleInspector(one.id)}
                />
                <span>
                  <span className="wo-worker__name">{one.name}</span>
                  {(one.title || one.tierName) && (
                    <span className="wo-worker__title">
                      {[one.title, one.tierName].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
          <div className="job-card-panel__foot">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => this.setState({ panel: null })}
            >
              {strings.cancel}
            </button>
            <ButtonLoader
              color="primary"
              loading={acting === "inspect"}
              disabled={Boolean(acting) || chosen.length === 0}
              onClick={this.sendToInspect}
            >
              {`${strings.send_to_inspect} (${chosen.length})`}
            </ButtonLoader>
          </div>
        </div>
      );
    }

    return null;
  }

  renderPhotos() {
    const { card, acting, deletingId } = this.state;
    const photos = card.photos || [];
    const canAdd = !this.isClosed && photos.length < MAX_PHOTOS;
    if (photos.length === 0 && this.isClosed) return null;

    return (
      <FormSection
        title={`${strings.images} (${photos.length}/${MAX_PHOTOS})`}
        icon="nc-icon nc-album-2"
      >
        <div className="job-card-photos">
          {photos.map((photo) => {
            const url = this.mediaUrl(photo.photoPath);
            return (
              <div key={photo.id} className="job-card-photo">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {isVideo(photo.photoPath) ? (
                    <video src={url} muted preload="metadata" />
                  ) : (
                    <img src={url} alt={card.plateNumber || strings.images} />
                  )}
                </a>
                {!this.isClosed && (
                  <button
                    type="button"
                    className="job-card-photo__remove"
                    title={strings.delete}
                    aria-label={strings.delete}
                    disabled={Boolean(acting)}
                    onClick={() => this.removeMedia(photo)}
                  >
                    <i
                      className={
                        deletingId === photo.id
                          ? "fa fa-spinner fa-spin"
                          : "nc-icon nc-simple-remove"
                      }
                    />
                  </button>
                )}
              </div>
            );
          })}
          {canAdd && (
            <label
              className={`job-card-media__pick${acting ? " is-disabled" : ""}`}
            >
              <i
                className={
                  acting === "photo" ? "fa fa-spinner fa-spin" : "nc-icon nc-simple-add"
                }
              />
              <span>{strings.photo_add}</span>
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                hidden
                disabled={Boolean(acting)}
                onChange={(event) => {
                  const { files } = event.target;
                  this.uploadMedia(files);
                  // cleared so the same file can be picked again
                  event.target.value = "";
                }}
              />
            </label>
          )}
        </div>
      </FormSection>
    );
  }

  renderIssues() {
    const { workOrder } = this.state;
    const items = (workOrder && workOrder.items) || [];
    if (items.length === 0) return null;
    const approved = items.filter((it) => it.status === ItemStatus.APPROVED).length;
    const symbol = currencySymbolOf(workOrder);

    return (
      <FormSection
        title={strings.formatString(strings.work_order_issues, approved, items.length)}
        icon="nc-icon nc-bullet-list-67"
      >
        {items.map((item) => {
          const meta = itemStatusMeta(item.status);
          return (
            <div key={item.id} className="job-card-issue">
              <span className={`job-card-issue__dot is-${meta.tone}`} />
              <span className="job-card-issue__text">
                <span className="job-card-issue__name">{item.name}</span>
                {item.description && (
                  <span className="job-card-issue__desc">{item.description}</span>
                )}
              </span>
              <span className="job-card-issue__side">
                {item.estimatedCost != null && (
                  <span className="job-card-issue__cost">
                    {money(item.estimatedCost, symbol)}
                  </span>
                )}
                <span className={`wo-pill is-${meta.tone}`}>{meta.label}</span>
              </span>
            </div>
          );
        })}
      </FormSection>
    );
  }

  renderPrevious() {
    const { previous } = this.state;
    if (previous.length === 0) return null;

    return (
      <FormSection title={strings.previous_visits} icon="fa fa-history">
        {previous.map((visit) => {
          const issues = visit.issues || [];
          return (
            <div key={visit.id} className="job-card-visit">
              <span className="job-card-visit__date">
                {new Date(visit.date).toLocaleDateString()}
              </span>
              <span className="job-card-visit__issues">
                {issues.length === 0
                  ? strings.no_issues_recorded
                  : issues.slice(0, 3).join(", ") +
                    (issues.length > 3 ? ` +${issues.length - 3}` : "")}
              </span>
              <span className={`wo-pill is-${statusTone(visit.status)}`}>
                {statusLabel(visit.status)}
              </span>
            </div>
          );
        })}
      </FormSection>
    );
  }

  render() {
    const { onClose } = this.props;
    const { card, error, success } = this.state;
    const status = strings[`job_card_${card.status}`] || card.status;
    const inspectorNames = (card.inspectors || []).map((w) => w.name).join(", ");

    return (
      <div className="mform job-card-view">
        {this.renderActions()}
        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: "" })}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" toggle={() => this.setState({ success: "" })}>
            {success}
          </Alert>
        )}
        {this.renderPanel()}

        <FormSection title={strings.vehicle} icon="nc-icon nc-delivery-fast">
          <FormGrid>
            <Detail label={strings.platenumber} value={card.plateNumber} />
            <Detail
              label={strings.make}
              value={[card.vehicleMake, card.vehicleModel].filter(Boolean).join(" ")}
            />
            <Detail label={strings.year} value={card.vehicleYear} />
            <Detail label={strings.arrivalmileage} value={formatMileage(card.mileage)} />
            {/* recorded on cards opened since the arrival check was added */}
            {card.fuelLevel !== null && card.fuelLevel !== undefined && (
              <Detail label={strings.fuel_level} value={`${card.fuelLevel}%`} />
            )}
            {card.deliveryMethodName && (
              <Detail
                label={strings.delivery_method}
                value={[
                  card.deliveryMethodName,
                  card.deliveryContactName,
                  card.deliveryContactPhone,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
            )}
            {inspectorNames && (
              <Detail label={strings.inspectors} value={inspectorNames} />
            )}
          </FormGrid>
        </FormSection>

        <FormSection title={strings.customer} icon="nc-icon nc-single-02">
          <FormGrid>
            <Detail label={strings.name} value={card.customerName} />
            <Detail label={strings.phone} value={card.customerPhone} />
            <Detail label={strings.worker} value={card.workerName} />
            <Detail
              label={strings.status}
              value={
                <span className={`job-card-status is-${card.status}`}>{status}</span>
              }
            />
          </FormGrid>
        </FormSection>

        {card.dropOffNotes && (
          <FormSection title={strings.note} icon="nc-icon nc-paper">
            <p className="job-card-notes">{card.dropOffNotes}</p>
          </FormSection>
        )}

        {this.renderPhotos()}
        {this.renderIssues()}
        {this.renderPrevious()}

        <div className="mform__footer">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {strings.cancel}
          </button>
        </div>
      </div>
    );
  }
}

export default JobCardView;
